import { GoodgameBitmapEngine } from "../GoodgameBitmapEngine";
import { GoodgameBitmapClip } from "../clip/GoodgameBitmapClip";
import { FrameStrip } from "../clipsource/FrameStrip";
import { IClipSource } from "../clipsource/IClipSource";
import { IExternalClipSource } from "../clipsource/IExternalClipSource";
import { ClipSizeComponent } from "../components/ClipSizeComponent";
import { IPoolable } from "../../../pooling/IPoolable";

import { Signal } from "signals.js";

/**
 *
 * plays and visualizes a framstrip with bitmapdata like MovieClip with dynamic loaded assets
 *
 * @author dkoehler
 *
 */
export class GoodgameBitmapClipExternal extends GoodgameBitmapClip implements IPoolable {
    protected _currentSource: IClipSource;

    /** Source of the Placeholder BitmapImages */
    private sourcePlaceholder: IClipSource;

    /** signal that actual loading queue is finished for the first time. name of this loader will be a parameter*/
    private _clipLoaded: Signal = new Signal(GoodgameBitmapClipExternal);

    /** signal that actual loading queue is finished for the first time. name of this loader will be a parameter*/
    public get clipLoaded(): Signal {
        return this._clipLoaded;
    }

    /** returns the current FrameContainer*/
    protected get frameStrip(): FrameStrip {
        if (this._currentSource) {
            return this._currentSource.frameStrip;
        }
        else {
            return null;
        }
    }

    protected get startFrame(): number {
        if (this._currentSource == this.sourcePlaceholder) {
            return 1;
        }
        else {
            return this._startFrame;
        }
    }

    public get endFrame(): number {
        if (!this._currentSource) {
            return 1;
        }
        else if (this._currentSource == this.sourcePlaceholder) {
            return this.sourcePlaceholder.frameStrip.frameAmount;
        }
        else {
            return this._endFrame;
        }

    }

    public set clipSizeComponent(clipSize: ClipSizeComponent) {
        this._clipSizeComponent = clipSize;

        if (this._clipSizeComponent && this._currentSource) {
            this._clipSizeComponent.setImageSize(this, this._currentSource.clipBounds);
            this._clipSizeComponent.clipSizeChanged.dispatch(this);
        }
    }

    /**
     * Constructor
     *
     * @param src source for the Animation
     * @param fps clip will be played with given fps
     * @param autoplay flag if clip will be started on creation
     * @param srcPlaceholder source for the PlaceholderAnimation
     *
     */
    constructor(src: IExternalClipSource = null, fps: number = 24, autoPlay: boolean = true, srcPlaceholder: IClipSource = null) {
        super((<IClipSource>src), fps, autoPlay);
        this.init();
        this.sourcePlaceholder = srcPlaceholder;
    }

    protected init(): void {
        let externalSource: IExternalClipSource = (<IExternalClipSource>this.source);
        this._currentSource = this.sourcePlaceholder;

        if (this._currentSource) {
            this.bmpImage.scaleX = this.bmpImage.scaleY = 1 / this._currentSource.scaleFactor;
        }

        if (externalSource) {
            if (!externalSource.isLoaded) {
                if (this.sourcePlaceholder) {
                    if (this.clipSizeComponent) {
                        this.clipSizeComponent.setImageSize(this, this._currentSource.clipBounds);
                    }
                    this.play();
                }
                externalSource.clipLoadedAndConverted.addOnce(this.bindFunction(this.onLoaded));
            }
            else {
                this.onLoaded(externalSource);
            }
        }
    }

    /**
     * inits the Clip when the source is fully loaded
     */
    private onLoaded(clipSource: IExternalClipSource): void {
        if (GoodgameBitmapEngine.instance.debugShowPlaceholder) {
            return;
        }
        this._currentSource = this.source;
        this.bmpImage.scaleX = this.bmpImage.scaleY = 1 / this._currentSource.scaleFactor;

        this.sourcePlaceholder = null;

        if (this.clipSizeComponent) {
            this.clipSizeComponent.setImageSize(this, this._currentSource.clipBounds);
            this.clipSizeComponent.clipSizeChanged.dispatch(this);
            //clipSizeComponent.clipSizeChanged.removeAll();
        }

        this._endFrame = this._currentSource.frameStrip.frameAmount;

        this.jumpToFrame(this._startFrame);

        if (this.frameStrip && this.frameStrip.hasMultipleFrames && this._running) {
            this.play();
        }
        else {
            this.stop();
        }

        this._clipLoaded.dispatch(this);
    }

    /** Source that is currently used */
    protected get currentSource(): IClipSource {
        return this._currentSource;
    }

    public dispose(): void {
        super.dispose();
        this.sourcePlaceholder = null;
        this._currentSource = null;
        this._clipLoaded.removeAll();
    }

    public get destroyed(): boolean {
        return false;
    }

    public renew(...parameters): void {
        this.sourcePlaceholder = parameters[0][3];
        this.source = parameters[0][0];
        this.targetFps = parameters[0][1];
        this._running = parameters[0][2];
        this.init();
    }

    public destroy(): void {
        this.dispose();
    }
}
